import { apiSlice } from "./apiSlice";
import {
    PROFESSION_URL,
    SPECIALTY_ATTRIBUTE_URL,
    SPECIALTY_ATTRIBUTE_ITEM_URL,
} from "../../constants/urls";
import { getTaxonomyEntityName } from "../../utils/taxonomyFields";

export const professionApiSlice = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getProfessions: builder.query({
            query: () => ({
                url: `${PROFESSION_URL}`,
                method: "GET",
            }),
            transformResponse: (response) => {
                const arr = Array.isArray(response?.data)
                    ? response.data
                    : Array.isArray(response)
                      ? response
                      : [];
                const seenById = new Set();
                const seenByName = new Set();
                const unique = arr.filter((c) => {
                    const id = c?.id ?? c?.Id;
                    const name = getTaxonomyEntityName(c).trim().toLowerCase();
                    if (id != null && seenById.has(id)) return false;
                    if (name && seenByName.has(name)) return false;
                    if (id != null) seenById.add(id);
                    if (name) seenByName.add(name);
                    return true;
                });
                return { ...response, data: unique };
            },
        }),
        getProfessionById: builder.query({
            serializeQueryArgs: ({ queryArgs }) => {
                const id = typeof queryArgs === "string" ? queryArgs : queryArgs?.id;
                const specialtyId =
                    typeof queryArgs === "string"
                        ? ""
                        : (queryArgs?.specialtyId ?? queryArgs?.branchId ?? "");
                return { type: "Profession", id, specialtyId: specialtyId || "" };
            },
            query: (arg) => {
                const id = typeof arg === "string" ? arg : arg?.id;
                const specialtyId =
                    typeof arg === "string" ? undefined : (arg?.specialtyId ?? arg?.branchId);
                const queryString = specialtyId
                    ? `?specialtyId=${encodeURIComponent(specialtyId)}`
                    : "";
                return {
                    url: `${PROFESSION_URL}/${id}${queryString}`,
                    method: "GET",
                };
            },
        }),
        createProfession: builder.mutation({
            query: (data) => ({
                url: `${PROFESSION_URL}`,
                method: "POST",
                body: {
                    name: data.name ?? data.specialtyCategoryName,
                    translate: data.translate,
                    defaultSessionDuration: data.defaultSessionDuration,
                    isEnabiz: data.isEnabiz,
                },
            }),
        }),
        updateProfession: builder.mutation({
            query: (data) => ({
                url: `${PROFESSION_URL}`,
                method: "PUT",
                body: {
                    id: data.id,
                    name: data.name ?? data.specialtyCategoryName,
                    translate: data.translate,
                    defaultSessionDuration: data.defaultSessionDuration,
                    isEnabiz: data.isEnabiz,
                },
            }),
        }),
        deleteProfession: builder.mutation({
            query: (id) => ({
                url: `${PROFESSION_URL}/${id}`,
                method: "DELETE",
            }),
        }),

        // Backend controller: SpecialtyCategoryAttribute (alias henüz yok)
        getSpecialtyAttributes: builder.query({
            query: () => ({
                url: `${SPECIALTY_ATTRIBUTE_URL}`,
                method: "GET",
            }),
        }),
        getSpecialtyAttributeById: builder.query({
            query: (id) => ({
                url: `${SPECIALTY_ATTRIBUTE_URL}/${id}`,
                method: "GET",
            }),
        }),
        createSpecialtyAttribute: builder.mutation({
            query: (data) => ({
                url: `${SPECIALTY_ATTRIBUTE_URL}`,
                method: "POST",
                body: {
                    professionId: data.professionId ?? data.specialtyCategoryId,
                    specialtyId: data.specialtyId ?? data.branchId ?? null,
                    kind: data.kind,
                    attributeDefinition: data.attributeDefinition,
                    attributeDefinitionTranslate: data.attributeDefinitionTranslate,
                    attributeDescription: data.attributeDescription,
                    attributeDescriptionTranslate: data.attributeDescriptionTranslate,
                    minSelectionCount: data.minSelectionCount,
                    maxSelectionCount: data.maxSelectionCount,
                },
            }),
        }),
        updateSpecialtyAttribute: builder.mutation({
            query: (data) => ({
                url: `${SPECIALTY_ATTRIBUTE_URL}/${data.id}`,
                method: "PUT",
                body: {
                    id: data.id,
                    professionId: data.professionId ?? data.specialtyCategoryId,
                    specialtyId: data.specialtyId ?? data.branchId ?? null,
                    kind: data.kind,
                    attributeDefinition: data.attributeDefinition,
                    attributeDefinitionTranslate: data.attributeDefinitionTranslate,
                    attributeDescription: data.attributeDescription,
                    attributeDescriptionTranslate: data.attributeDescriptionTranslate,
                    minSelectionCount: data.minSelectionCount,
                    maxSelectionCount: data.maxSelectionCount,
                },
            }),
        }),
        deleteSpecialtyAttribute: builder.mutation({
            query: (id) => ({
                url: `${SPECIALTY_ATTRIBUTE_URL}/${id}`,
                method: "DELETE",
            }),
        }),

        getSpecialtyAttributeItems: builder.query({
            query: () => ({
                url: `${SPECIALTY_ATTRIBUTE_ITEM_URL}`,
                method: "GET",
            }),
        }),
        getSpecialtyAttributeItemById: builder.query({
            query: (id) => ({
                url: `${SPECIALTY_ATTRIBUTE_ITEM_URL}/${id}`,
                method: "GET",
            }),
        }),
        getSpecialtyAttributeItemsByAttribute: builder.query({
            query: (attributeId) => ({
                url: `${SPECIALTY_ATTRIBUTE_ITEM_URL}/ByAttributeId/${attributeId}`,
                method: "GET",
            }),
        }),
        createSpecialtyAttributeItem: builder.mutation({
            query: (data) => ({
                url: `${SPECIALTY_ATTRIBUTE_ITEM_URL}`,
                method: "POST",
                body: {
                    itemText: data.itemText,
                    translate: data.translate,
                    specialtyCategoryAttributeId:
                        data.specialtyAttributeId ?? data.specialtyCategoryAttributeId,
                },
            }),
        }),
        updateSpecialtyAttributeItem: builder.mutation({
            query: (data) => ({
                url: `${SPECIALTY_ATTRIBUTE_ITEM_URL}`,
                method: "PUT",
                body: {
                    id: data.id,
                    itemText: data.itemText,
                    translate: data.translate,
                    specialtyCategoryAttributeId:
                        data.specialtyAttributeId ?? data.specialtyCategoryAttributeId,
                },
            }),
        }),
        deleteSpecialtyAttributeItem: builder.mutation({
            query: (id) => ({
                url: `${SPECIALTY_ATTRIBUTE_ITEM_URL}/${id}`,
                method: "DELETE",
            }),
        }),
    }),
});

export const {
    useGetProfessionsQuery,
    useGetProfessionByIdQuery,
    useLazyGetProfessionByIdQuery,
    useCreateProfessionMutation,
    useUpdateProfessionMutation,
    useDeleteProfessionMutation,
    useGetSpecialtyAttributesQuery,
    useGetSpecialtyAttributeByIdQuery,
    useLazyGetSpecialtyAttributeByIdQuery,
    useCreateSpecialtyAttributeMutation,
    useUpdateSpecialtyAttributeMutation,
    useDeleteSpecialtyAttributeMutation,
    useGetSpecialtyAttributeItemsQuery,
    useGetSpecialtyAttributeItemByIdQuery,
    useLazyGetSpecialtyAttributeItemByIdQuery,
    useGetSpecialtyAttributeItemsByAttributeQuery,
    useLazyGetSpecialtyAttributeItemsByAttributeQuery,
    useCreateSpecialtyAttributeItemMutation,
    useUpdateSpecialtyAttributeItemMutation,
    useDeleteSpecialtyAttributeItemMutation,
} = professionApiSlice;
